/**
 * Filter and sort utilities for PreparedDropsDraftsList.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { PreparedDrop } from '@/hooks';
import { countryLocalSendSortValue } from '@/utils/country-timezone';
import type { SentSection, SortKey } from '../types';

export const NO_TAGS_VALUE = '__none__';

/** Filters via the SAME resolver the rows display with, so filter and display
 *  always agree (per-campaign scoped on the Prepared Drops tab, blended-map
 *  fallback elsewhere). */
export function filterDropsByTag(
  drops: PreparedDrop[],
  tagFilter: string,
  tagsForDrop?: (drop: PreparedDrop) => string[] | undefined,
): PreparedDrop[] {
  if (!tagFilter || !tagsForDrop) return drops;
  return drops.filter((d) => {
    const tags = tagsForDrop(d) ?? [];
    if (tagFilter === NO_TAGS_VALUE) return tags.length === 0;
    return tags.includes(tagFilter);
  });
}

/**
 * Keeps drops carrying ANY of the selected static tags. `NO_TAGS_VALUE` matches
 * drops with no static tag, so it combines with real tags ("CIRO or untagged").
 * An empty selection means no filtering.
 */
export function filterDropsByStaticTag(
  drops: PreparedDrop[],
  staticTagFilters: string[],
): PreparedDrop[] {
  if (staticTagFilters.length === 0) return drops;
  const wanted = new Set(staticTagFilters);
  const includeUntagged = wanted.has(NO_TAGS_VALUE);
  return drops.filter((d) => {
    if (!d.staticTag) return includeUntagged;
    return wanted.has(d.staticTag.name);
  });
}

export function filterDropsBySearch(
  drops: PreparedDrop[],
  searchLower: string,
  getGatewayName: (d: PreparedDrop) => string,
  getLeadsName: (d: PreparedDrop) => string,
): PreparedDrop[] {
  const words = searchLower ? searchLower.split(/\s+/).filter(Boolean) : [];
  if (words.length === 0) return drops;
  return drops.filter((d) => {
    const gw = getGatewayName(d);
    const ll = getLeadsName(d);
    const searchable = [
      d.titleBase,
      d.sentTitle,
      gw,
      String(d.routeId),
      ll,
      String(d.leadsListWiserId),
      d.countryKey ?? '',
      d.campaignName ?? '',
      d.shortLinkTldValue,
      String(d.amount),
      d.status,
      d.sentStatusLabel ?? '',
      ...(d.senderNames ?? []),
      ...(d.smsTexts ?? []),
      d.id,
      d.workDate,
      d.sentDropUniqueId ?? '',
      d.sentDropId != null ? String(d.sentDropId) : '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
    return words.every((word) => searchable.includes(word));
  });
}

/**
 * Section-aware time value for sorting:
 * - sent: sentAt (when the drop was actually sent)
 * - scheduled: executeAfter (the target execution time)
 * - killed: finishedAt (when the drop was stopped)
 * Falls back through the chain if the primary field is missing.
 */
export function getTimeValue(d: PreparedDrop, section?: SentSection): string {
  if (section === 'scheduled') return d.executeAfter || d.sentAt || d.createdAt || '';
  if (section === 'killed') return d.finishedAt || d.sentAt || d.createdAt || '';
  return d.sentAt || d.finishedAt || d.executeAfter || d.createdAt || '';
}

/** Sort each country block separately, then concatenate (A→Z by country key). */
export function sortDropsGroupedByCountry(
  drops: PreparedDrop[],
  sortBy: SortKey,
  sortDir: 'asc' | 'desc',
  gatewayName: (d: PreparedDrop) => string,
  leadsName: (d: PreparedDrop) => string,
  gatewayPrice: (d: PreparedDrop) => number,
  sentSection?: SentSection,
): PreparedDrop[] {
  const by = new Map<string, PreparedDrop[]>();
  for (const d of drops) {
    const k = d.countryKey ?? '';
    if (!by.has(k)) by.set(k, []);
    by.get(k)!.push(d);
  }
  return [...by.keys()]
    .sort((a, b) => a.localeCompare(b))
    .flatMap((ck) => sortDrops(by.get(ck)!, sortBy, sortDir, gatewayName, leadsName, gatewayPrice, sentSection));
}

export function sortDrops(
  drops: PreparedDrop[],
  sortBy: SortKey,
  sortDir: 'asc' | 'desc',
  gatewayName: (d: PreparedDrop) => string,
  leadsName: (d: PreparedDrop) => string,
  gatewayPrice: (d: PreparedDrop) => number,
  sentSection?: SentSection,
): PreparedDrop[] {
  const mult = sortDir === 'asc' ? 1 : -1;
  return [...drops].sort((a, b) => {
    const gwA = gatewayName(a);
    const gwB = gatewayName(b);
    const leadsA = leadsName(a);
    const leadsB = leadsName(b);
    const costA = gatewayPrice(a);
    const costB = gatewayPrice(b);
    const totalA = a.amount * costA;
    const totalB = b.amount * costB;
    const creativeA = [...(a.senderNames ?? []), ...(a.smsTexts ?? [])].join(' ');
    const creativeB = [...(b.senderNames ?? []), ...(b.smsTexts ?? [])].join(' ');
    const statusA = a.sentStatusLabel ?? a.status;
    const statusB = b.sentStatusLabel ?? b.status;

    let cmp = 0;
    switch (sortBy) {
      case 'country':
        cmp = (a.countryKey ?? '').localeCompare(b.countryKey ?? '');
        break;
      case 'gateway':
        cmp = gwA.localeCompare(gwB);
        break;
      case 'leads':
        cmp = leadsA.localeCompare(leadsB);
        break;
      case 'campaign':
        cmp = (a.campaignName ?? '').localeCompare(b.campaignName ?? '');
        break;
      case 'cost':
        cmp = costA - costB;
        break;
      case 'amount':
        cmp = a.amount - b.amount;
        break;
      case 'totalCost':
        cmp = totalA - totalB;
        break;
      case 'tld':
        cmp = (a.shortLinkTldValue ?? '').localeCompare(b.shortLinkTldValue ?? '');
        break;
      case 'staticTag':
        cmp = (a.staticTag?.name ?? '').localeCompare(b.staticTag?.name ?? '');
        break;
      case 'created':
        cmp = (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
        break;
      case 'scheduled':
        cmp = (a.executeAfter ?? '').localeCompare(b.executeAfter ?? '');
        break;
      case 'localSend':
        cmp = countryLocalSendSortValue(a.executeAfter, a.countryKey).localeCompare(
          countryLocalSendSortValue(b.executeAfter, b.countryKey),
        );
        break;
      case 'time':
        cmp = getTimeValue(a, sentSection).localeCompare(getTimeValue(b, sentSection));
        break;
      case 'creative':
        cmp = creativeA.localeCompare(creativeB);
        break;
      case 'status':
        cmp = statusA.localeCompare(statusB);
        break;
      default:
        return 0;
    }
    return cmp * mult;
  });
}
